/**
 * Heuristic per-cell bounding-box for the Sibit Diäten (expense) form.
 *
 * Diäten-Spiegel zu cellBbox.ts (Stundenliste). GENAU dasselbe Muster wie dort:
 * feste Spalten-% + festes Zeilen-Raster über das Tabellen-Band → DPI-unabhängige
 * Prozente, die das PdfWithHighlights-Overlay rendert.
 *
 * Kalibrierung: gegen die TATSÄCHLICHE PDF-Vorschau (api/cards/expense/:id/preview,
 * Landscape-Render des Glückshofer/Haider-Diäten-Templates) via cv2-Grid-Line-
 * Detection vermessen — NICHT aus den OCR-internen `_naive_expense_grid`-Werten
 * (die gelten für das gedrehte/zugeschnittene OCR-Bild, nicht für die Vorschau).
 * Genauso wie cellBbox.ts „Calibrated against Glückshofer Feb-2026" — die Geometrie
 * ist template-/mandanten-spezifisch.
 *
 * Unterschied zur Stundenliste: dort ist die Zeile = Tag (1:1, fixe 31 Tage,
 * data.days[d]). Bei Diäten ist die Zeile = ENTRY-INDEX (data.entries[i]) über das
 * fixe Zeilen-Raster — exakt für ein voll ausgefülltes Formular (Entries dicht von
 * oben), approximativ bei Lücken. Pixel-genaue Zeilen kommen später über einen
 * cv2-Grid-Detect (analog timesheet /grid-bboxes), genau wie die Stundenliste mit
 * Heuristik begann und per Backend-Detect verfeinert wurde.
 */

export type ExpenseFieldName =
  | "tag" | "datum" | "kz_nr" | "kunde" | "zm_abzug" | "m_verr" | "ort" | "abfahrt" | "rueckkehr";

/** Zeilen-Raster (cv2-vermessen): erste Datenzeile bei 26.4%, Zeilen-Pitch 3.17%. */
const ROW_TOP_PCT = 26.4;
const ROW_HEIGHT_PCT = 3.17;
export const EXPENSE_MAX_ROWS = 24;

/** Spalten-Ranges (0-100% der Vorschau-Breite), cv2 + visuell kalibriert. */
const COL_RANGES: Record<ExpenseFieldName, [number, number]> = {
  tag:       [6.0, 9.5],
  datum:     [9.5, 18.0],
  kz_nr:     [18.0, 29.5],
  kunde:     [29.5, 50.0],
  zm_abzug:  [50.0, 58.0],
  m_verr:    [58.0, 66.0],
  ort:       [66.0, 80.0],
  abfahrt:   [80.0, 89.0],
  rueckkehr: [89.0, 97.0],
};

export interface BboxPct {
  topPct: number;
  leftPct: number;
  widthPct: number;
  heightPct: number;
}

/** Heuristik-Bbox für (Zeilen-Index, Feld). rowIndex ist 0-basiert (entry-Index). */
export function expenseCellBboxPct(rowIndex: number, field: ExpenseFieldName): BboxPct | null {
  if (rowIndex < 0 || rowIndex >= EXPENSE_MAX_ROWS) return null;
  const range = COL_RANGES[field];
  if (!range) return null;
  return {
    topPct: ROW_TOP_PCT + rowIndex * ROW_HEIGHT_PCT,
    leftPct: range[0],
    widthPct: range[1] - range[0],
    heightPct: ROW_HEIGHT_PCT,
  };
}

/** Ganze Zeile (alle Spalten) für Hover-Hervorhebung der kompletten Reise-Zeile. */
export function expenseRowBboxPct(rowIndex: number): BboxPct | null {
  if (rowIndex < 0 || rowIndex >= EXPENSE_MAX_ROWS) return null;
  return {
    topPct: ROW_TOP_PCT + rowIndex * ROW_HEIGHT_PCT,
    leftPct: COL_RANGES.tag[0],
    widthPct: COL_RANGES.rueckkehr[1] - COL_RANGES.tag[0],
    heightPct: ROW_HEIGHT_PCT,
  };
}
